import React from 'react'
import { useHistory, useLocation } from 'react-router'
import { AuthForm, AuthFormSubmitValues } from '@local/do-secundo-auth-form'
import { LoginPrompt } from '@local/do-secundo-identity-prompts'
import { PoweredByToastModal } from '../../../../client/components/PoweredByToastModal/PoweredByToastModal'
import { unifiedLoginCreateAccountValidationSchema } from '../../../../client/utils/form-schemas'
import { useIsAccountLinkingEnabled } from '@local/do-secundo-passwordless-authentication'
import {
  deleteTokens,
  accountLinkingStorage
} from '../../../../client/apollo/authentication/authentication-helpers'
import cx from 'classnames'
import { getRawPhoneNumberWithCountryCode } from '@local/do-secundo-form-utils'

// styles for terms of service links
import styles from '../../../../client/components/AuthModals/CreateAccountModal/CreateAccountModal.module.css'
import { Button, ButtonType, ButtonVariant } from '@local/do-secundo-button'
import { useAuth } from '../../../../client/components/AuthProvider/AuthProvider'
import { dataByTypename } from '../../../../client/utils/apollo-helpers'
import { FROM_LOCATION } from '../utils/enums'

export interface UnifiedLoginCreateAccountModalProps {
  onClose: () => void
  buttonText?: string
  includeEmail?: boolean
  handleSubmitError?: (error: { Error: string }) => void
}

export const UnifiedLoginCreateAccountModal = ({
  onClose,
  handleSubmitError,
  buttonText = 'Continue',
  includeEmail = false
}: UnifiedLoginCreateAccountModalProps) => {
  const { startPasswordlessLogin } = useAuth()
  const history = useHistory()
  const ACCOUNT_LINKING_ENABLED = useIsAccountLinkingEnabled()
  const { state = {}, search } = useLocation()
  const handleSubmit = async (values: AuthFormSubmitValues) => {
    const resp = await startPasswordlessLogin({
      variables: {
        input: {
          phone: getRawPhoneNumberWithCountryCode(values.phone)
        }
      }
    })

    const { StartIdentityProfileError } = dataByTypename(
      resp.data.startPasswordlessLogin
    )

    if (StartIdentityProfileError) {
      throw Error(StartIdentityProfileError.message)
    }

    history.push('?mode=sms', {
      from: FROM_LOCATION.create,
      guestPhone: values.phone
    })
  }
  const phone = state.guestPhone || state.basicInfo?.phone || ''
  const headerText =
    search === '?mode=create'
      ? 'Enter your phone number'
      : 'Confirm your phone number'
  const dataIdSuffix = search === '?mode=login' ? 'login' : 'create'

  // we need to delete accountLinkingStorage because they may have abandoned
  // the account linking flow previously and now their credentials are out of date
  // and will result in throwing an InvalidJWT.
  React.useEffect(() => {
    deleteTokens(accountLinkingStorage)
  }, [])

  const footer =
    ACCOUNT_LINKING_ENABLED && search === '?mode=login' ? (
      <div className={'pt-4 text-center'}>
        <Button
          data-testid='login-passwordless-modal-legacy-login'
          type={ButtonType.BUTTON}
          variant={ButtonVariant.SECONDARY}
          responsive
          onClick={() => history.push('?mode=connectLogin')}
        >
          Continue with email
        </Button>
      </div>
    ) : null

  const disclaimer =
    search === '?mode=create' ? (
      <p className='type-caption'>
        We will send a one-time verification code to set up your account. Your
        phone number will be used to authenticate and provide you with automated
        messages like order status updates.
      </p>
    ) : null

  return (
    <PoweredByToastModal
      className='h-full'
      onClose={onClose}
      header={<h2 className='mx-0 mt-0 mb-3 type-headline-4'>{headerText}</h2>}
    >
      <div
        className='flex flex-col justify-between h-full m-auto'
        data-testid='sign-up-prompt-container'
      >
        <LoginPrompt
          form={
            <AuthForm
              handleSubmit={handleSubmit}
              initialValues={{ phone }}
              validationSchema={unifiedLoginCreateAccountValidationSchema}
              buttonText={buttonText}
              includeEmail={includeEmail}
              handleSubmitError={handleSubmitError}
              testId={`unified-${dataIdSuffix}-auth-form`}
              disclaimer={disclaimer}
            />
          }
          footer={footer}
        />
      </div>
    </PoweredByToastModal>
  )
}
